import { defineStore, storeToRefs } from 'pinia'
import { computed, ref, shallowRef } from 'vue'
import { useDeviceStore } from './device'
import { BaseDevice, FirmwareEvents, VersionInfo } from '@rapoo/device-sdk'

export const useFirmwareStore = defineStore('firmware', () => {
  const deviceStore = useDeviceStore()
  const { currentDevice, deviceList } = storeToRefs(deviceStore)

  // 标准设备版本缓存列表
  const versionsMap = ref<Map<string, VersionInfo[]>>(new Map())

  const checkLatestVersion = async (device?: BaseDevice | null) => {
    const targetDevice = device ?? currentDevice.value
    if (!targetDevice) return
    const id = targetDevice.id
    const firmware = targetDevice.features.firmware
    if (!firmware) {
      return
    }
    try {
      const versionInfos = await firmware.checkFirmwareUpdate()
      versionsMap.value.set(id, versionInfos)
    } catch (_error) {
      try {
        const versionInfos = await firmware.getFirmwareVersion()
        versionsMap.value.set(id, versionInfos)
      } catch (fallbackError) {
        // 底座/鼠标刚重枚举时 WebHID 常短暂 NotAllowed，避免未捕获 Promise 刷红
        console.warn(
          `[firmware] checkLatestVersion skipped (${targetDevice.manifest.model}):`,
          fallbackError,
        )
      }
    }
  }

  const canUpdateFirmwares = (device?: BaseDevice | null) => {
    const targetDevice = device ?? currentDevice.value
    if (!targetDevice) return undefined
    const id = targetDevice.id
    const firmware = targetDevice.features.firmware
    if (!firmware) {
      return undefined
    }
    const versionInfos = versionsMap.value.get(id)
    if (!versionInfos) {
      return undefined
    }
    return versionInfos.filter((info) => info.canUpdate)
  }

  /* ----------------- 固件更新 ---------------- */
  const currentDeviceCanUpdate = computed(() => canUpdateFirmwares(currentDevice.value)?.length)
  const currentDeviceCanUpdateFirmware = computed(() => canUpdateFirmwares(currentDevice.value))
  /** 获取指定设备的版本信息（优先从缓存读取，无缓存时主动检查） */
  const getDeviceVersions = async (device: BaseDevice): Promise<VersionInfo[] | null> => {
    const cached = versionsMap.value.get(device.id)
    if (cached) return cached
    await checkLatestVersion(device)
    return versionsMap.value.get(device.id) ?? null
  }

  const currentVersions = computed(() => {
    if (!currentDevice.value) return undefined
    const firmware = currentDevice.value.features.firmware
    if (!firmware) return undefined
    const versionInfos = versionsMap.value.get(currentDevice.value.id)
    if (!versionInfos) return undefined
    return versionInfos
  })
  const firmwareUpgradeProgress = ref(0)
  const isFirmwareUpdating = ref(false)
  /** 全屏升级进度弹窗（App 级挂载，首页恢复升级与设置页共用） */
  const showUpgradeProgressDialog = ref(false)
  /**
   * 升级锁定的设备实例：进 Boot 断连时仍保持为 currentDevice，
   * 避免自动切到另一台；离开升级页回首页前必须 release。
   */
  const upgradingDevice = shallowRef<BaseDevice | null>(null)

  const handleProgress = (data: { progress: number }) => {
    const { progress } = data
    const prev = firmwareUpgradeProgress.value
    firmwareUpgradeProgress.value = progress
    if (Math.floor(progress) !== Math.floor(prev) || progress === 0 || progress >= 100) {
      console.info('[FW-DIAG] progress', {
        progress,
        prev,
        model: upgradingDevice.value?.manifest.model ?? currentDevice.value?.manifest.model,
      })
    }
  }

  const getProgressDevice = () => upgradingDevice.value ?? currentDevice.value

  const registerUpgradeProgress = () => {
    getProgressDevice()?.on(FirmwareEvents.WritedData, handleProgress)
  }
  const unregisterUpgradeProgress = () => {
    getProgressDevice()?.off(FirmwareEvents.WritedData, handleProgress)
  }

  /** 开始升级时锁定目标设备（按实例，勿用 model） */
  function lockUpgradingDevice(device: BaseDevice) {
    upgradingDevice.value = device
  }

  function isUpgradingDevice(device: BaseDevice | null | undefined): boolean {
    return Boolean(device && upgradingDevice.value && upgradingDevice.value.id === device.id)
  }

  /**
   * 离开升级页、回首页前调用：解除锁定，并清掉已断连的选中设备，避免首页挂僵尸。
   */
  function releaseUpgradingDeviceBeforeHome() {
    const locked = upgradingDevice.value
    upgradingDevice.value = null
    if (!locked) return

    const stillCurrent = currentDevice.value?.id === locked.id
    if (!stillCurrent) return

    const listed = deviceList.value.some((d) => d.id === locked.id)
    const connected = Boolean(locked.channel?.isConnected)
    if (!listed || !connected) {
      currentDevice.value = deviceList.value[0] ?? null
    }
  }

  function openUpgradeProgressDialog() {
    showUpgradeProgressDialog.value = true
  }

  function closeUpgradeProgressDialog() {
    showUpgradeProgressDialog.value = false
  }

  return {
    versionsMap,
    checkLatestVersion,
    getDeviceVersions,
    canUpdateFirmwares,

    currentDeviceCanUpdate,
    currentDeviceCanUpdateFirmware,
    currentVersions,
    firmwareUpgradeProgress,
    isFirmwareUpdating,
    showUpgradeProgressDialog,
    openUpgradeProgressDialog,
    closeUpgradeProgressDialog,
    upgradingDevice,
    lockUpgradingDevice,
    isUpgradingDevice,
    releaseUpgradingDeviceBeforeHome,
    registerUpgradeProgress,
    unregisterUpgradeProgress,
  }
})
